const JSON_ESCAPE_CHARACTERS = new Set(['"', '\\', '/', 'b', 'f', 'n', 'r', 't']);
// eslint-disable-next-line no-control-regex
const CONTROL_CHARACTERS = /[\u0000-\u0008\u000B\u000C\u000E-\u001F]+/g;
const MOJIBAKE_SEQUENCE = /(?:Ã.|Â.|â..|ð...|ï¿½)/gu;
const HTML_SPACE_ENTITY = /&#(?:x20|32);/gi;

// Characters commonly produced when UTF-8 bytes are decoded as Windows-1252.
const WINDOWS_1252_BYTES = Object.freeze({
  0x20AC: 0x80,
  0x201A: 0x82,
  0x192: 0x83,
  0x201E: 0x84,
  0x2026: 0x85,
  0x2020: 0x86,
  0x2021: 0x87,
  0x2C6: 0x88,
  0x2030: 0x89,
  0x160: 0x8A,
  0x2039: 0x8B,
  0x152: 0x8C,
  0x17D: 0x8E,
  0x2018: 0x91,
  0x2019: 0x92,
  0x201C: 0x93,
  0x201D: 0x94,
  0x2022: 0x95,
  0x2013: 0x96,
  0x2014: 0x97,
  0x2DC: 0x98,
  0x2122: 0x99,
  0x161: 0x9A,
  0x203A: 0x9B,
  0x153: 0x9C,
  0x17E: 0x9E,
  0x178: 0x9F
});

const decodeWindows1252Utf8 = (value) => {
  const bytes = [];
  for (const character of value) {
    const codePoint = character.codePointAt(0);
    const byte = codePoint <= 0xFF ? codePoint : WINDOWS_1252_BYTES[codePoint];
    if (byte === undefined) {
      return null;
    }
    bytes.push(byte);
  }

  try {
    if (typeof TextDecoder === 'function') {
      return new TextDecoder('utf-8', { fatal: true }).decode(new Uint8Array(bytes));
    }
    return decodeURIComponent(bytes.map((byte) => `%${byte.toString(16).padStart(2, '0')}`).join(''));
  } catch (error) {
    return null;
  }
};

const containsMojibake = (value) => {
  MOJIBAKE_SEQUENCE.lastIndex = 0;
  const result = MOJIBAKE_SEQUENCE.test(value);
  MOJIBAKE_SEQUENCE.lastIndex = 0;
  return result;
};

const repairMojibake = (value) => value.replace(MOJIBAKE_SEQUENCE, (sequence) => {
  const decoded = decodeWindows1252Utf8(sequence);
  if (!decoded || containsMojibake(decoded)) {
    return sequence;
  }
  return decoded;
});

/**
 * Repairs text corruption that can make an otherwise useful JSON value hard
 * to parse or read. Unknown control characters are replaced with one visible
 * replacement marker because their original accented character is not
 * recoverable from the control byte alone.
 */
export const repairTextEncoding = (value) => {
  if (typeof value !== 'string') {
    return value;
  }

  return repairMojibake(value)
    .replace(CONTROL_CHARACTERS, '\uFFFD')
    .replace(HTML_SPACE_ENTITY, ' ');
};

const repairJsonSyntax = (value) => {
  let repaired = '';
  let insideString = false;

  for (let index = 0; index < value.length; index += 1) {
    const character = value[index];

    if (!insideString) {
      repaired += character;
      if (character === '"') {
        insideString = true;
      }
      continue;
    }

    if (character === '"') {
      repaired += character;
      insideString = false;
      continue;
    }

    if (character === '\\') {
      const nextCharacter = value[index + 1];
      if (JSON_ESCAPE_CHARACTERS.has(nextCharacter)) {
        repaired += character + nextCharacter;
        index += 1;
        continue;
      }
      if (
        nextCharacter === 'u'
        && /^[0-9a-f]{4}$/i.test(value.slice(index + 2, index + 6))
      ) {
        repaired += value.slice(index, index + 6);
        index += 5;
        continue;
      }

      // Markdown escapes such as \* are not JSON escapes. Keep the visible
      // character and remove only the invalid backslash.
      if (nextCharacter !== undefined) {
        repaired += nextCharacter;
        index += 1;
      } else {
        repaired += '\\\\';
      }
      continue;
    }

    if (CONTROL_CHARACTERS.test(character)) {
      CONTROL_CHARACTERS.lastIndex = 0;
      while (index + 1 < value.length && CONTROL_CHARACTERS.test(value[index + 1])) {
        CONTROL_CHARACTERS.lastIndex = 0;
        index += 1;
      }
      repaired += '\uFFFD';
      continue;
    }
    CONTROL_CHARACTERS.lastIndex = 0;

    // Literal line breaks and tabs are also invalid inside JSON strings.
    if (character === '\n') {
      repaired += '\\n';
    } else if (character === '\r') {
      repaired += '\\r';
    } else if (character === '\t') {
      repaired += '\\t';
    } else {
      repaired += character;
    }
  }

  return repaired;
};

const stripJsonCodeFence = (value) => value
  .replace(/^```(?:json)?\s*/i, '')
  .replace(/\s*```$/i, '')
  .trim();

const repairParsedValue = (value) => {
  if (typeof value === 'string') {
    return repairTextEncoding(value);
  }
  if (Array.isArray(value)) {
    return value.map(repairParsedValue);
  }
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [repairTextEncoding(key), repairParsedValue(item)])
    );
  }
  return value;
};

const looksLikeJsonContainer = (value) => /^(?:\[|{)/.test(value.trim());

/**
 * Parses JSON values while tolerating common extraction/encoding damage.
 * Valid JSON is returned unchanged apart from recursive text normalization.
 */
export const parseJsonValueWithRepair = (value, maxDepth = 3) => {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value !== 'string') {
    return repairParsedValue(value);
  }

  let current = value;
  for (let depth = 0; depth <= maxDepth; depth += 1) {
    const trimmed = current.trim();
    if (!trimmed) {
      return null;
    }

    const candidates = [trimmed];
    const unfenced = stripJsonCodeFence(trimmed);
    if (unfenced !== trimmed) {
      candidates.push(unfenced);
    }

    let parsed = null;
    let parsedSuccessfully = false;
    for (const candidate of candidates) {
      try {
        parsed = JSON.parse(candidate);
        parsedSuccessfully = true;
        break;
      } catch (error) {
        try {
          parsed = JSON.parse(repairJsonSyntax(candidate));
          parsedSuccessfully = true;
          break;
        } catch (repairedError) {
          // Try the next candidate, if the value was fenced.
        }
      }
    }

    if (!parsedSuccessfully) {
      return null;
    }
    if (typeof parsed !== 'string' || !looksLikeJsonContainer(parsed) || depth >= maxDepth) {
      return repairParsedValue(parsed);
    }
    current = parsed;
  }

  return null;
};
